import Link from 'next/link';
import { getCdnUrl } from '../../lib/cdnUrl';
import { usePublicClient, useWalletClient } from 'wagmi';
import { useEffect, useState } from 'react';
import { BigNumber } from 'ethers';
import { getContract } from 'wagmi/actions';
import erc712MintABI from '../../lib/erc721MintAbi';
import erc1155MintAbi from '../../lib/erc1155MintAbi';
import { formatEther } from 'ethers/lib/utils.js';
import CollectionBadges from '../collectrions/collection_badges';
import { ChainIcon } from '../Icon';
import { getTokenName } from '../../helpers/getChainName';
import ShareDropdown from '../dropdown/Share_dropdown';
import wbnbABI from '../../helpers/wbnbABI';
import { getContractType } from '../../helpers/getContractType';
import { erc404MintAbi } from '../../lib/erc404MintAbi';
import { dn404MintAbi } from '../../lib/dn404MintAbi';

export const MintingCard = ({ minting }) => {
  const {
    id,
    status,
    totalSupply: totalSupplyDb,
    maxSupply: maxSupplyDb,
    collection,
    publicPrice,
    mintTokenName,
  } = minting;
  const [maxSupply, setMaxSupply] = useState(maxSupplyDb);
  const [totalSupply, setTotalSupply] = useState(BigNumber.from(totalSupplyDb || 0));
  const [price, setPrice] = useState(publicPrice);
  const signer = useWalletClient();
  const publicClient = usePublicClient();
  const { isErc1155, isErc404, isErc721, isDn404 } = getContractType(collection?.contractType);

  let mintAbi = erc712MintABI;
  if (isErc1155) {
    mintAbi = erc1155MintAbi;
  } else if (isErc404) {
    mintAbi = erc404MintAbi;
  } else if (isDn404) {
    mintAbi = dn404MintAbi;
  }

  useEffect(() => {
    (async () => {
      if (!collection) return;

      let erc20Contract = null;
      if (isDn404 || isErc404) {
        erc20Contract = getContract({
          chainId: collection.chainId,
          address: collection.address,
          abi: mintAbi,
        });
      }
      try {
        if (isErc721 || isErc404 || isDn404) {
          const contract = getContract({
            address: collection.address,
            abi: isErc404 ? erc404MintAbi : erc712MintABI,
            publicClient,
            walletClient: signer.data,
          });

          setMaxSupply(isErc404 ? 0 : await contract.read.maxSupply().catch(() => maxSupplyDb));
          let supply = 0;
          if (isErc404) {
            supply = await contract.read.erc721TotalSupply().catch(() => totalSupplyDb || 0);
          } else if (isDn404) {
            supply = await erc20Contract.read.totalMinted().catch(() => totalSupplyDb || 0);
          } else {
            supply = await contract.read.totalSupply().catch(() => totalSupplyDb || 0);
          }
          setTotalSupply(supply);
          setPrice(
            formatEther(await contract.read.price().catch(() => publicPrice)).replace(/\.?0+$/, ''),
          );
        } else if (isErc1155) {
          const contract = getContract({
            address: collection.address,
            abi: erc1155MintAbi,
            publicClient,
            walletClient: signer.data,
          });
          setMaxSupply(await contract.read.maxSupply());
          setTotalSupply(await contract.read.totalSupply([1]));
          setPrice(formatEther(await contract.read.mintPrice()).replace(/\.?0+$/, ''));
        }
      } catch (error) {
        console.log('MintingCard', error);
      }
    })();
  }, [signer.data, collection]);

  return (
    <article
      key={id}
      className='text-jacarta-700 transition-colors duration-200 hover:text-accent-dark dark:text-jacarta-100 dark:hover:text-accent-light'
    >
      <Link href={`/nft/minting/${collection.address}`}>
        <a>
          <div className='rounded-2.5xl relative block border border-jacarta-100 bg-white p-[1.1875rem] pb-20 [transition:border-color_.3s,box-shadow_.3s] hover:border-jacarta-500 hover:shadow-lg dark:border-jacarta-700 dark:bg-jacarta-700 dark:hover:border-jacarta-500'>
            <figure className='relative'>
              <img
                src={
                  minting?.mintingPhotoPath || collection?.coverPhotoPath
                    ? getCdnUrl(
                        minting?.mintingPhotoPath ?? collection.coverPhotoPath,
                        'w560',
                        collection?.updatedAt,
                      )
                    : '/images/collections/_Bit5_U_Cover_1920x300.png'
                }
                alt='item 5'
                className='h-[160px] w-full rounded-[0.625rem] object-cover'
              />

              <div className='absolute -bottom-3 right-3 flex items-center justify-center sm:-bottom-3'>
                <div className='flex'>
                  <img
                    src={
                      collection?.profilePhotoPath
                        ? getCdnUrl(collection.profilePhotoPath, 'thumbnail', collection?.updatedAt)
                        : '/images/no_image.png'
                    }
                    alt='creator'
                    className='rounded-xl h-16 w-16 border-2 border-white object-cover dark:border-jacarta-600 sm:h-24 sm:w-24'
                  />
                </div>
              </div>
            </figure>
            <div className='mt-3 flex flex-col justify-center'>
              <span className='flex items-center truncate font-display text-lg font-bold'>
                <span className='mr-2 flex items-center pt-1'>{collection.name}</span>
                <CollectionBadges
                  isVerified={collection.isVerified}
                  isDNFT={collection.isDNFT}
                  placement={'bottom'}
                />
                <div>
                  <ShareDropdown
                    buttonWidth={25}
                    roundedLg
                    link={`${
                      typeof window !== 'undefined' ? window.location.origin : ''
                    }/nft/minting/${collection.address}`}
                  />
                </div>
              </span>
              <span
                className={
                  'mt-2 font-display text-base tracking-widest' +
                  (status === 'live'
                    ? ' text-green'
                    : status === 'upcoming'
                    ? ' text-blue'
                    : ' text-red')
                }
              >
                {status.toUpperCase()}
              </span>
            </div>
            <div className='mt-3 h-10 text-sm'>
              <span className='max-h-[48px] overflow-hidden text-jacarta-500 [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] dark:text-jacarta-300'>
                {collection.description}
              </span>
            </div>
            <div className='mt-3 flex items-center justify-between'>
              <div className='flex items-center'>
                {publicPrice && publicPrice > 0 ? (
                  <span className={'contents font-semibold text-jacarta-500 dark:text-jacarta-300'}>
                    Mint Price:&nbsp;
                    <ChainIcon
                      name={mintTokenName?.toLowerCase() ?? collection.chain}
                      tooltip={mintTokenName ?? getTokenName(collection.chain)}
                      sideGap={true}
                    />
                    {publicPrice}
                  </span>
                ) : (
                  <span className='font-semibold text-accent'>Free Mint</span>
                )}
              </div>
            </div>

            <div className='absolute bottom-2 left-1/2 flex max-h-[60px] w-full -translate-x-1/2 transform flex-wrap items-center justify-between space-x-3 truncate border-t border-jacarta-100 px-5 pt-2 dark:border-jacarta-500'>
              <div className='w-3/5 grow'>
                <span className='truncate text-lg font-semibold text-jacarta-500 dark:text-jacarta-300'>
                  Minted: {Number(totalSupply) || 0} / {maxSupply || maxSupplyDb}
                </span>
                <div className='mt-2 mb-2 h-2 w-full rounded-full bg-jacarta-200 dark:bg-jacarta-600'>
                  <div
                    className='h-full rounded-full bg-jacarta-500 dark:bg-jacarta-200'
                    style={{ width: `${((Number(totalSupply) || 0) / maxSupply) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </a>
      </Link>
    </article>
  );
};

export const TokenMintingCard = ({ minting }) => {
  const {
    id,
    status,
    totalSupply: totalSupplyDb,
    maxSupply: maxSupplyDb,
    publicPrice,
    mintToken,
    mintTokenName,
    tokenName,
    tokenAddress,
    tokenSymbol,
  } = minting;
  const [maxSupply, setMaxSupply] = useState(maxSupplyDb);
  const [totalSupply, setTotalSupply] = useState(BigNumber.from(totalSupplyDb || 0));
  const [price, setPrice] = useState(publicPrice);
  const signer = useWalletClient();
  const isIse = minting.mintType === 'ise';

  useEffect(() => {
    (async () => {
      try {
        const contract = getContract({
          address: tokenAddress,
          abi: wbnbABI,
          walletClient: signer.data,
          chainId: minting.chainId,
        });

        setMaxSupply(await contract.read.maxSupply());
        setTotalSupply(await contract.read.totalSupply());
        setPrice(formatEther(await contract.read.price()).replace(/\.?0+$/, ''));
      } catch (error) {
        console.log('MintingCard', error);
      }
    })();
  }, [signer.data, minting]);

  return (
    <article
      key={id}
      className='text-jacarta-700 transition-colors duration-200 hover:text-accent-dark dark:text-jacarta-100 dark:hover:text-accent-light'
    >
      <Link href={`/token/minting/${tokenAddress}`}>
        <a>
          <div className='rounded-2.5xl relative block border border-jacarta-100 bg-white p-[1.1875rem] [transition:border-color_.3s,box-shadow_.3s] hover:border-jacarta-500 hover:shadow-lg dark:border-jacarta-700 dark:bg-jacarta-700 dark:hover:border-jacarta-500'>
            <figure className='relative'>
              <img
                src={
                  minting?.mintingPhotoPath || minting?.bannerPhotoPath
                    ? getCdnUrl(
                        minting?.mintingPhotoPath || minting?.bannerPhotoPath,
                        'w560',
                        minting?.updatedAt,
                      )
                    : '/images/collections/_Bit5_U_Cover_1920x300.png'
                }
                alt='item 5'
                className='h-[160px] w-full rounded-[0.625rem] object-cover'
              />

              <div className='absolute -bottom-3 right-3 flex items-center justify-center sm:-bottom-3'>
                <div className='flex'>
                  <img
                    src={
                      minting?.profilePhotoPath
                        ? getCdnUrl(minting.profilePhotoPath, 'thumbnail', minting?.updatedAt)
                        : '/images/no_image.png'
                    }
                    alt='creator'
                    className='rounded-xl h-16 w-16 border-2 border-white object-cover dark:border-jacarta-600 sm:h-24 sm:w-24'
                  />
                </div>
              </div>
            </figure>
            <div className='mt-3 flex flex-col justify-center'>
              <span className='flex items-center truncate font-display text-lg font-bold'>
                <span className='mr-2 flex items-center pt-1'>{minting.tokenName}</span>
                <div>
                  <ShareDropdown
                    buttonWidth={25}
                    roundedLg
                    link={`${
                      typeof window !== 'undefined' ? window.location.origin : ''
                    }/token/minting/${minting.tokenAddress}`}
                  />
                </div>
              </span>
              <span
                className={
                  'mt-2 font-display text-base tracking-widest' +
                  (status === 'live'
                    ? ' text-green'
                    : status === 'upcoming'
                    ? ' text-blue'
                    : ' text-red')
                }
              >
                {status.toUpperCase()}
              </span>
            </div>
            <div className='mt-3 h-10 text-sm'>
              <span className='max-h-[48px] overflow-hidden text-jacarta-500 [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] dark:text-jacarta-300'>
                {minting?.description}
              </span>
            </div>
            <div className='mt-3 flex items-center justify-between'>
              <div className='flex items-center'>
                {/* {price && price > 0 ? (
                  <span className={'contents font-semibold text-jacarta-500 dark:text-jacarta-300'}>
                    Mint Price:&nbsp;
                    <ChainIcon
                      name={mintTokenName?.toLowerCase() ?? minting?.chain}
                      tooltip={mintTokenName ?? getTokenName(minting?.chain)}
                      sideGap={true}
                    />
                    {price}
                  </span>
                ) : (
                  <span className='font-semibold text-accent'>Free Mint</span>
                )} */}
                {isIse ? (
                  <span className={'contents font-semibold text-jacarta-500 dark:text-jacarta-300'}>
                    ISE&nbsp;
                    <ChainIcon
                      name={mintToken ?? minting?.chain}
                      tooltip={mintTokenName ?? getTokenName(minting?.chain)}
                      sideGap={true}
                    />
                  </span>
                ) : null}
              </div>
            </div>

            {/* <div className='absolute bottom-2 left-1/2 flex max-h-[60px] w-full -translate-x-1/2 transform flex-wrap items-center justify-between space-x-3 truncate border-t border-jacarta-100 px-5 pt-2 dark:border-jacarta-500'>
              <div className='w-3/5 grow'>
                <span className='truncate text-lg font-semibold text-jacarta-500 dark:text-jacarta-300'>
                  Minted: {Number(totalSupply) || 0} / {maxSupply || maxSupplyDb}
                </span>
                <div className='mt-2 mb-2 h-2 w-full rounded-full bg-jacarta-200 dark:bg-jacarta-600'>
                  <div
                    className='h-full rounded-full bg-jacarta-500 dark:bg-jacarta-200'
                    style={{ width: `${((Number(totalSupply) || 0) / maxSupply) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div> */}
          </div>
        </a>
      </Link>
    </article>
  );
};
